import {
  Autocomplete,
  Button,
  Checkbox,
  // Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  // ListItem,
  // ListItemIcon,
  // ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
  InputAdornment,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import { CustomFormField } from "@utilifiles/component/CustomFormElement";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "react-query";
import {
  // getDrugs,
  getPackageDetails,
  getProcedure,
  savePackage,
} from "./services";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import AddPackage from "./AddPackage";
import {
  AddValuesType,
  // DrugSearchType,
  FormValueType,
  PackageTableDataType,
  ProcedureDataType,
  ProcedureType,
  SubDepartment,
} from "./types";
import useAuthentication from "@hooks/useAuthentication";
import SaveIcon from "@mui/icons-material/Save";
import usePopup from "@hooks/usePopup";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import CheckIcon from "@mui/icons-material/Check";

export const formatArrayData = (data: AddValuesType[]): SubDepartment[] => {
  if (!data?.length) return [];

  const grouped = data.reduce<Record<number, SubDepartment>>((acc, item) => {
    const subDeptId = item?.subDepartment?.subDeptId as keyof typeof acc;

    // Initialize SubDepartment if not present
    if (!acc[subDeptId]) {
      acc[subDeptId] = {
        subDeptId,
        categories: [],
        extraCptIds: [],
      };
    }

    if (item.category) {
      // Find or create category
      let category = acc[subDeptId].categories.find(
        (c) => c.categoryId === item.category!.categoryId
      );

      if (!category) {
        category = {
          categoryId: item.category.categoryId,
          cptIds: [],
        };
        acc[subDeptId].categories.push(category);
      }

      // Add CPTs to category
      category.cptIds.push(
        ...item.cpt.map((c) => ({
          ...c,
          cptRate: Number(c?.cptRate || 0),
          packageRate: Number(c?.packageRate || 0),
        }))
      );
    } else {
      // Add CPTs to extraCptIds when category is null
      acc[subDeptId].extraCptIds.push(
        ...item.cpt.map((c) => ({
          ...c,
          cptRate: Number(c?.cptRate || 0),
          packageRate: Number(c?.packageRate || 0),
        }))
      );
    }

    return acc;
  }, {});
  return Object.values(grouped);
};

const TitleComponent = ({ title }: { title: string }) => {
  return (
    <div className="col-span-full flex justify-between gap-2 p-2 bg-blue-50 rounded">
      <Typography
        sx={{
          display: "inline-block",
          color: "primary.main",
          borderRadius: "5px",
          px: 1.5,
          py: 0.5,
          fontSize: "0.75rem",
          fontWeight: 600,
          lineHeight: 1.5,
          minWidth: "6vw",
        }}
      >
        {title}
      </Typography>
    </div>
  );
};
const pageTitle = "Package page";
function AddOEditPackage() {
  const navigate = useNavigate();
  const { user } = useAuthentication();
  const { showSnackbar } = usePopup();
  const form = useForm<FormValueType>({
    defaultValues: {
      packageCode: "",
      packageName: "",
      packageDescription: "",
      isLabPackage: 0,
      packageStatus: 1,
      packageActualRate: 0,
      packageDiscount: 0,
      packageFinalRate: 0,
      prescription: [],
      procedure: [],
    },
  });

  const { state } = useLocation() as { state: PackageTableDataType | null };

  const [radiologyPackageList, setRadiologyPackageList] = useState<
    AddValuesType[]
  >([]);
  const [labPackageList, setLabPackageList] = useState<AddValuesType[]>([]);
  const [procedureList, setProcedureList] = useState<ProcedureDataType[]>([]);
  const [selectedProcedures, setSelectedProcedures] = useState<
    ProcedureDataType[]
  >([]);
  const [procedureParams, setProcedureParams] = useState<ProcedureType>({
    pageNumber: 0,
    filter: { searchText: "", status: 2, cptGrpId: 7 }, // cpt group id ( 7 ) is for getting drug and procedure in the cpt field of procedure 
    pageSize: 25,
    sortField: "",
    sortOrder: "",
  });

  const [actualRate, setActualRate] = useState<{
    radiology: number;
    lab: number;
    procedure: number;
  }>({
    radiology: 0,
    lab: 0,
    procedure: 0,
  });

  const [packageRate, setPackageRate] = useState<{
    radiology: number;
    lab: number;
    procedure: number;
  }>({
    radiology: 0,
    lab: 0,
    procedure: 0,
  });

  const [selectedRadiologyValue, setSelectedRadiologyValue] =
    useState<AddValuesType>({
      subDepartment: null,
      category: null,
      cpt: [],
    });

  const [selectedLabValue, setSelectedLabValue] = useState<AddValuesType>({
    subDepartment: null,
    category: null,
    cpt: [],
  });

  const [selectedCpt, setSelectedCpt] = useState<number | null>(null);
  const [editRate, setEditRate] = useState<number | null>(null);

  const { data: procedures, isLoading: proceduresLoading } = useQuery(
    [`${pageTitle}procedurescpt`, procedureParams.filter.searchText],
    async () => getProcedure({...procedureParams,codetype:String(user?.codetype)}),
    {
      refetchOnWindowFocus: false,
    }
  );

  const { mutate: fetchDetails, isLoading: detailsLoading } = useMutation(
    getPackageDetails,
    {
      onSuccess: (result) => {
        if (result.responseIndicator == "success") {
          setLabPackageList(result.response.lab);
          setProcedureList(result.response.procedure);
          form.setValue("procedure", result?.response?.procedure || []);
          form.setValue("prescription", result?.response?.prescription || []);
          let radiology: AddValuesType[] = [];
          let radiologyARate = 0;
          let radiologyPRate = 0;
          let labARate = 0;
          let labPRate = 0;
          let procedureARate = 0;
          let procedurePRate = 0;
          result.response.radiology.map((item) => {
            if (Array.isArray(item.category)) {
              item.category.forEach((c) => {
                radiology.push({
                  subDepartment: item.subDepartment,
                  category: c,
                  cpt: c.cpt,
                });
                radiologyARate += c.cpt.reduce(
                  (sum: any, i: any) => sum + (Number(i?.cptRate) || 0),
                  0
                );
                radiologyPRate += c.cpt.reduce(
                  (sum: any, i: any) => sum + Number(i?.packageRate),
                  0
                );
              });
            }

            if (item?.extraCpt?.length) {
              radiology.push({
                subDepartment: item.subDepartment,
                category: null,
                cpt: item?.extraCpt || [],
              });
              radiologyARate += (item?.extraCpt || []).reduce(
                (sum: any, i: any) => sum + Number(i.cptRate),
                0
              );
              radiologyPRate += (item?.extraCpt || []).reduce(
                (sum: any, i: any) => sum + Number(i.packageRate),
                0
              );
            }
          });

          let lab: AddValuesType[] = [];
          result.response.lab.map((item) => {
            if (Array.isArray(item.category)) {
              item.category.forEach((c) => {
                lab.push({
                  subDepartment: item.subDepartment,
                  category: c?.categoryId || null,
                  cpt: c.cpt,
                });
                labARate += c.cpt.reduce(
                  (sum: any, i: any) => sum + (Number(i?.cptRate) || 0),
                  0
                );
                labPRate += c.cpt.reduce(
                  (sum: any, i: any) => sum + Number(i.packageRate),
                  0
                );
              });
            }

            if (item?.extraCpt?.length) {
              lab.push({
                subDepartment: item.subDepartment,
                category: null,
                cpt: item?.extraCpt || [],
              });
              labARate += (item?.extraCpt || []).reduce(
                (sum: any, i: any) => sum + Number(i.cptRate),
                0
              );
              labPRate += (item?.extraCpt || []).reduce(
                (sum: any, i: any) => sum + Number(i.packageRate),
                0
              );
            }
          });

          procedureARate = result?.response?.procedure?.reduce(
            (sum, i) => sum + Number(i?.cptRate || 0),
            0
          );
          procedurePRate = result?.response?.procedure?.reduce(
            (sum, i) => sum + Number(i?.packageRate || 0),
            0
          );

          setActualRate({
            lab: labARate,
            procedure: procedureARate,
            radiology: radiologyARate,
          });

          setPackageRate({
            lab: labPRate,
            procedure: procedurePRate,
            radiology: radiologyPRate,
          });

          setRadiologyPackageList(radiology);
          setLabPackageList(lab);
        } else {
          showSnackbar({
            open: true,
            message: result.responseMessage,
            variant: "warning",
            duration: 3000,
          });
        }
      },
      onError: (e: any) => {
        showSnackbar({
          open: true,
          message:
            e?.message || "Something went wrong please refresh the page!",
          variant: "warning",
          duration: 3000,
        });
      },
    }
  );

  const { mutate: save, isLoading: saveLoading } = useMutation(savePackage, {
    onSuccess: (result) => {
      if (result.responseIndicator == "success") {
        showSnackbar({
          open: true,
          message: result.responseMessage,
          variant: "success",
          duration: 3000,
        });
        handleGoBack();
      } else {
        showSnackbar({
          open: true,
          message: result.responseMessage,
          variant: "warning",
          duration: 3000,
        });
      }
    },
    onError: (e: any) => {
      showSnackbar({
        open: true,
        message: e.message,
        variant: "error",
        duration: 3000,
      });
    },
  });

  const handleGoBack = () => {
    navigate(-1);
  };

  const onSubmit = (value: FormValueType) => {
    let procedure = {
      cptGroupId: 4,
      cptIds: value?.isLabPackage ? [] : procedureList,
    };

    save({
      packageId: state?.packageId || null,
      clinicId: Number(user?.clinicId),
      createdById: Number(user?.userId),
      isLabPackage: Boolean(value.isLabPackage) ? 1 : 0,
      packageActualRate: value?.packageActualRate || 0,
      packageCode: value.packageCode,
      packageDescription: value.packageDescription,
      packageDiscount: value?.packageDiscount || 0,
      packageFinalRate: value?.packageFinalRate || 0,
      packageName: value.packageName,
      packageStatus: value.packageStatus,
      lab: {
        deptId: Number(labPackageList[0]?.subDepartment?.deptId),
        cptGroupId: 2,
        subDepartments: formatArrayData(labPackageList),
      },
      radiology: {
        deptId: Number(radiologyPackageList[0]?.subDepartment?.deptId),
        cptGroupId: 1,
        subDepartments: formatArrayData(radiologyPackageList),
      },
      procedure,
    });
  };

  const onSubmitError = (e: any) => {
    console.log("Form error ====>", e);
  };

  const handleReset = () => {
    form.reset({
      packageCode: "",
      packageName: "",
      packageDescription: "",
      isLabPackage: 0,
      packageStatus: 1,
      packageActualRate: 0,
      packageDiscount: 0,
      packageFinalRate: 0,
      prescription: [],
      procedure: [],
    });
    setActualRate({ lab: 0, procedure: 0, radiology: 0 });
    setPackageRate({ lab: 0, procedure: 0, radiology: 0 });
    setRadiologyPackageList([]);
    setLabPackageList([]);
    setProcedureList([]);
    setSelectedProcedures([]);
    setSelectedLabValue({
      cpt: [],
      category: null,
      subDepartment: null,
    });
    setSelectedRadiologyValue({
      cpt: [],
      category: null,
      subDepartment: null,
    });
  };

  const handleRadiologyChange = (value: AddValuesType[]) => {
    setRadiologyPackageList(value);
    let aRate = 0;
    let pRate = 0;
    value.forEach((p) => {
      p.cpt.forEach((c) => {
        aRate += Number(c.cptRate);
        pRate += Number(c.packageRate);
      });
    });
    setActualRate((prev) => ({ ...prev, radiology: aRate }));
    setPackageRate((prev) => ({ ...prev, radiology: pRate }));
    form.setValue(
      "packageActualRate",
      Number((actualRate.lab + actualRate.procedure + aRate).toFixed(2))
    );
    form.setValue(
      "packageFinalRate",
      Number((packageRate.lab + packageRate.procedure + pRate).toFixed(2))
    );
  };

  const handleLabChange = (value: AddValuesType[]) => {
    setLabPackageList(value);
    let aRate = 0;
    let pRate = 0;
    value.forEach((p) => {
      p.cpt.forEach((c) => {
        aRate += Number(c.cptRate);
        pRate += Number(c.packageRate);
      });
    });
    setActualRate((prev) => ({ ...prev, lab: aRate }));
    setPackageRate((prev) => ({ ...prev, lab: pRate }));
    form.setValue(
      "packageActualRate",
      Number((actualRate.radiology + actualRate.procedure + aRate).toFixed(2))
    );
    form.setValue(
      "packageFinalRate",
      Number((packageRate.radiology + packageRate.procedure + pRate).toFixed(2))
    );
  };

  const handleRemoveCpt = (index: number) => {
    let aRate = 0;
    let pRate = 0;
    let items = procedureList.filter((_, i) => index != i);
    items.forEach((c) => {
      aRate += Number(c.cptRate || 0);
      pRate += Number(c.packageRate || 0);
    });
    setActualRate((prev) => ({ ...prev, procedure: aRate }));
    setPackageRate((prev) => ({ ...prev, procedure: pRate }));
    form.setValue(
      "packageActualRate",
      Number((actualRate.radiology + actualRate.lab + aRate).toFixed(2))
    );
    form.setValue(
      "packageFinalRate",
      Number((packageRate.radiology + packageRate.lab + pRate).toFixed(2))
    );
    setProcedureList(items);
  };

  const handleAddCpt = () => {
    let aRate = 0;
    let pRate = 0;
    const update = [...selectedProcedures, ...procedureList].map((c) => {
      const obj = { ...c };
      obj.cptRate = c?.cptRate || 0;
      obj.packageRate = c?.packageRate || c?.cptRate || 0;
      aRate += Number(obj.cptRate || 0);
      pRate += Number(obj.packageRate || 0);
      return obj;
    });
    setProcedureList(update);
    console.log("update in handleAddCpt => ",update);
    setActualRate((prev) => ({ ...prev, procedure: aRate }));
    setPackageRate((prev) => ({ ...prev, procedure: pRate }));
    form.setValue(
      "packageActualRate",
      Number((actualRate.radiology + actualRate.lab + aRate).toFixed(2))
    );
    form.setValue(
      "packageFinalRate",
      Number((packageRate.radiology + packageRate.lab + pRate).toFixed(2))
    );
    setSelectedProcedures([]);
  };

  const handleRateEdit = () => {
    const update = [...procedureList];
    const cpt = {
      ...update[selectedCpt as number],
    };

    let difference = Number(cpt?.packageRate) - Number(editRate);
    difference = packageRate.procedure - difference;
    cpt.packageRate = Number(editRate);
    setPackageRate((prev) => ({ ...prev, procedure: difference }));
    form.setValue(
      "packageFinalRate",
      Number((packageRate.radiology + packageRate.lab + difference).toFixed(2))
    );
    update[selectedCpt as number] = cpt;
    setProcedureList(update);
    setEditRate(0);
    setSelectedCpt(null);
  };

  const handleCancelEdit = () => {
    setEditRate(0);
    setSelectedCpt(null);
  };

  useEffect(() => {
    if (state) {
      form.reset({
        ...state,
        packageDiscount: 0,
        prescription: [],
        procedure: [],
      });
      fetchDetails({
        clinicId: Number(user?.clinicId),
        packageId: state?.packageId,
      });
    }
  }, [state]);

  return (
    <div className="p-2">
      <Paper>
        <form onSubmit={form.handleSubmit(onSubmit, onSubmitError)}>
          <div className="p-2 flex justify-between items-center border-b-2">
            <Typography fontWeight={500}>Package master add</Typography>
            <div className="space-x-2 ">
              <Button
                disabled={saveLoading}
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleGoBack}
              >
                back
              </Button>
              {!Boolean(state) && (
                <Button
                  disabled={saveLoading}
                  color="error"
                  type="reset"
                  onClick={handleReset}
                  startIcon={<CloseIcon />}
                >
                  reset
                </Button>
              )}
              <Button
                disabled={saveLoading}
                type="submit"
                color="success"
                startIcon={
                  saveLoading ? (
                    <CircularProgress size={20} color="success" />
                  ) : (
                    <SaveIcon />
                  )
                }
              >
                {Boolean(state) ? "update" : "save"}
              </Button>
            </div>
          </div>
          <div className="p-2">
            <div className="flex flex-col gap-2">
              <div className="grid grid-cols-3 gap-3">
                <CustomFormField
                  control={form.control}
                  name="packageCode"
                  element="input"
                  fieldProps={{ label: "Package code" }}
                  rules={{ required: "Package code " }}
                />
                <CustomFormField
                  control={form.control}
                  name="packageName"
                  element="input"
                  fieldProps={{ label: "Package name" }}
                  rules={{ required: "Package name " }}
                />
                <CustomFormField
                  control={form.control}
                  name="packageDescription"
                  element="input"
                  fieldProps={{ label: "Enter description " }}
                  rules={{ required: false }}
                />
                {/* <div> */}
                <CustomFormField
                  control={form.control}
                  name="packageActualRate"
                  element="input"
                  fieldProps={{
                    label: "Enter actual rate ",
                    InputProps: { readOnly: true },
                  }}
                  rules={{ required: "Actual rate required" }}
                />
                <div className="hidden">
                  <CustomFormField
                    control={form.control}
                    name="packageDiscount"
                    element="input"
                    fieldProps={{ label: "Discount " }}
                    rules={{ required: "Discount required" }}
                  />
                </div>
                <CustomFormField
                  control={form.control}
                  name="packageFinalRate"
                  element="input"
                  fieldProps={{
                    label: "Final rate ",
                    InputProps: { readOnly: true },
                  }}
                  rules={{ required: "Final required" }}
                />
                {/* </div> */}
                <CustomFormField
                  control={form.control}
                  name="isLabPackage"
                  element="checkbox"
                  fieldProps={{ label: "Lab package" }}
                  rules={{ required: false }}
                />
                <CustomFormField
                  control={form.control}
                  name="packageStatus"
                  element="radio"
                  radios={[
                    { label: "Active", value: 1 },
                    { label: "Inactive", value: 0 },
                  ]}
                  radioProps={{ row: true }}
                  fieldProps={{ label: "Status" }}
                  rules={{ required: false }}
                />
              </div>
              <div className="col-span-full mt-2 p-2 border-2 rounded-md">
                <TitleComponent title="Radiology" />
                <AddPackage
                  selectedList={radiologyPackageList}
                  onAdd={handleRadiologyChange}
                  departmentId={3}
                  cptGroupId={1}
                  loading={detailsLoading}
                  selectedValue={selectedRadiologyValue}
                  setSelectedValue={setSelectedRadiologyValue}
                />
              </div>
              <div className="col-span-full mt-2 p-2 border-2 rounded-md">
                <TitleComponent title="Lab" />
                <AddPackage
                  selectedList={labPackageList}
                  onAdd={handleLabChange}
                  departmentId={5}
                  cptGroupId={2}
                  loading={detailsLoading}
                  selectedValue={selectedLabValue}
                  setSelectedValue={setSelectedLabValue}
                />
              </div>

              {!form.watch("isLabPackage") && (
                <>
                  <Divider className="col-span-full" />
                  <div className="col-span-full grid gap-3 mt-2 p-2">
                    <TitleComponent title="Procedure" />
                    <div className="grid grid-cols-12">
                      <div className="col-span-5 flex justify-center items-center">
                        <Autocomplete
                          fullWidth
                          multiple
                          disableCloseOnSelect
                          blurOnSelect={false}
                          disabled={
                            !Boolean(procedureParams.filter.searchText) &&
                            proceduresLoading
                          }
                          value={selectedProcedures || []}
                          options={procedures?.response?.items || []}
                          loading={proceduresLoading}
                          filterOptions={(option) => option}
                          getOptionLabel={(option) =>
                            `( ${option.cptCode} ) ${option.cptName}`
                          }
                          isOptionEqualToValue={(option, value) =>
                            option.cptId === value.cptId
                          }
                          onChange={(_, cpt) => setSelectedProcedures(cpt)}
                          onInputChange={(_, searchText) => {
                            setProcedureParams((prev) => ({
                              ...prev,
                              filter: { ...prev.filter, searchText },
                            }));
                          }}
                          renderInput={(params) => (
                            <TextField {...params} label="Select CPT" />
                          )}
                          renderOption={(props, option, { selected }) => (
                            <ListItem {...props} disablePadding>
                              <ListItemIcon>
                                <Checkbox checked={selected} />
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <div className="grid grid-cols-7">
                                    <Typography
                                      color={"primary"}
                                      className="col-span-2"
                                    >
                                      {option?.cptCode}
                                    </Typography>
                                    <Typography className="col-span-4">
                                      {option.cptName}
                                    </Typography>
                                  </div>
                                }
                              />
                            </ListItem>
                          )}
                        />
                      </div>
                      <div className="flex justify-center items-center">
                        <Button
                          endIcon={<KeyboardDoubleArrowRightIcon />}
                          onClick={handleAddCpt}
                        >
                          add
                        </Button>
                      </div>
                      <div className="col-span-6 border-2 rounded-md grid grid-cols-1 gap-3 transition-all">
                        <Table size="small">
                          <TableBody>
                            {procedureList?.map((cpt, i) => (
                              <TableRow key={`${cpt?.cptId}-${i}`}>
                                <TableCell width={400}>{cpt.cptName}</TableCell>
                                <TableCell>
                                  <TextField
                                    value={
                                      selectedCpt === i
                                        ? editRate ?? ""
                                        : cpt?.packageRate || cpt?.cptRate || 0
                                    }
                                    fullWidth
                                    onFocus={() => {
                                      setSelectedCpt(i);
                                      setEditRate(cpt?.packageRate || cpt?.cptRate || 0);
                                    }}
                                    onChange={(e) => {
                                      setEditRate(e.target.value as any);
                                    }}
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment
                                          position="end"
                                          className="flex gap-2"
                                        >
                                          {selectedCpt == i && (
                                            <>
                                              <IconButton
                                                sx={{ padding: 0 }}
                                                color="success"
                                                onClick={handleRateEdit}
                                              >
                                                <CheckIcon
                                                  sx={{ fontSize: "1.2rem" }}
                                                />
                                              </IconButton>
                                              <IconButton
                                                sx={{ padding: 0 }}
                                                color="error"
                                                onClick={handleCancelEdit}
                                              >
                                                <CloseIcon
                                                  sx={{ fontSize: "1.2rem" }}
                                                />
                                              </IconButton>
                                            </>
                                          )}
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <IconButton
                                    size="small"
                                    color="warning"
                                    className="transition-transform hover:scale-110"
                                    onClick={() => {
                                      handleRemoveCpt(i);
                                    }}
                                  >
                                    <RemoveCircleIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </form>
      </Paper>
    </div>
  );
}

export default AddOEditPackage;
